import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Grid, Skeleton } from '@mui/material';

import Title from '@Components/Title';
import { userLogin } from '@Services/hasAdminAccess';
import { isActionAllowed } from '../utils/allowedActions';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import { UserObj } from '@Reducers/AccountLookup/Type';

import { getCreditLimit, getOutstandingBalanceForPostpaid } from '@Reducers/Credit';
import { getOutstandingBalanceInvoiceForPostpaid } from '@Reducers/Credit/actions';
import { getOutstandingBalanceForPrepaid, getWalletBalance } from '@Reducers/Wallet';

// Lazy-loaded: admin-only and user-only views are mutually exclusive per render,
// so splitting them keeps the initial bundle down for whichever branch loads.
const TopPerformanceView = lazy(() => import('@Components/AdminHome/TopPerformanceView'));
const TrackingStatusAdmin = lazy(() => import('@Components/AdminHome/TrackingStatus'));
const GeneralOverview = lazy(() => import('@Components/AdminHome/GeneralOverview'));
const TopTenView = lazy(() => import('@Components/AdminHome/TopTenView'));
const GrossProfitGraph = lazy(() => import('@Components/AdminHome/GrossProfitGraph'));

const TrackingStatusUser = lazy(() => import('@Components/UserHome/TrackingStatus'));
const TodayShipment = lazy(() => import('@Components/UserHome/TodayShipment'));
const UserGeneralOverview = lazy(() => import('@Components/UserHome/UserGeneralOverview'));
const UserDestinations = lazy(() => import('@Components/UserHome/UserDestinations'));

const NavigationButtons = lazy(() => import('./ShipmentHistory/Navigation'));

// Named exports need the `.then` remap for React.lazy (which expects a default export)
const Navigation = lazy(() => import('./Transactions/Credit').then(module => ({ default: module.Navigation })));
const NavigationWallet = lazy(() => import('./Transactions/Wallet').then(module => ({ default: module.Navigation })));

// Reusable Suspense fallback — a fixed height per section keeps the layout
// stable while the lazy chunk loads (avoids CLS from content popping in).
const SectionFallback = ({ height = 120 }: { height?: number }) => (
  <Skeleton variant="rounded" width="100%" height={height} sx={{ borderRadius: '10px' }} />
);

function HomeRoutes() {
  const isUserLogin = userLogin();
  const dispatch = useDispatch<AppDispatch>();

  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);
  const isPrepaid = useSelector((state: RootStateType) => state.loginUser.profile?._accountId.isPrepaid);
  const [accountUser] = useState<UserObj | null>(null);
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  // Memoized so these booleans are only recomputed when the underlying actions change,
  // instead of on every render of HomeRoutes.
  const isUpdateLimitEditAllowed = useMemo(
    () => isActionAllowed(accessGroupById?.actions, ACTION_NAMES.Credits_And_Wallet.update_limit, PERFORM_ACTION.write),
    [accessGroupById?.actions],
  );

  const isAddMoneyEditAllowed = useMemo(
    () => isActionAllowed(accessGroupById?.actions, ACTION_NAMES.Credits_And_Wallet.add_money, PERFORM_ACTION.write),
    [accessGroupById?.actions],
  );

  // Stable reference so NavigationWallet doesn't see a new `refetch` prop on every render
  const handleRefetch = useCallback(() => {
    console.log('check');
  }, []);

  useEffect(() => {
    if (!isUserLogin) return;
    if (isPrepaid) {
      dispatch(getOutstandingBalanceForPrepaid());
      if (isUserLogin) dispatch(getWalletBalance());
    } else {
      dispatch(getCreditLimit());
      dispatch(getOutstandingBalanceForPostpaid());
      dispatch(getOutstandingBalanceInvoiceForPostpaid());
    }
  }, [isUserLogin, isPrepaid, dispatch]);

  return (
    <>
      <Box>
        <Grid color={'primary.main'} item xs={12}>
          <Title title={`Welcome to ${name?.toUpperCase()}!`} removeBorder />
        </Grid>
        {!isUserLogin ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Suspense fallback={<SectionFallback height={280} />}>
              <TopPerformanceView />
              <TrackingStatusAdmin />
              <GeneralOverview />
              <GrossProfitGraph />
              <TopTenView />
            </Suspense>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Box>
              <Box sx={{ width: '100%' }}>
                <Suspense fallback={<SectionFallback height={60} />}>
                  <NavigationButtons />
                </Suspense>
              </Box>
              <Box
                sx={{
                  padding: '30px',
                  border: '2px solid #f6f6f6',
                  borderRadius: '10px',
                  boxShadow: '0px 1px 4px 0px #00000040',
                  marginTop: '10px',
                }}
              >
                <Suspense fallback={<SectionFallback height={180} />}>
                  {isPrepaid ? (
                    <>
                      <p style={{ fontSize: '24px', fontWeight: '500', marginBottom: '32px' }}>Wallet Details</p>
                      <NavigationWallet
                        selectedUser={accountUser}
                        refetch={handleRefetch}
                        disabled={!isAddMoneyEditAllowed}
                      />
                    </>
                  ) : (
                    <>
                      <p style={{ fontSize: '24px', fontWeight: '500', marginBottom: '32px' }}>Credit Details</p>
                      <Navigation selectedUser={accountUser} disabled={!isUpdateLimitEditAllowed} />
                    </>
                  )}
                </Suspense>
              </Box>
            </Box>
            <Box
              sx={{
                padding: '10px',
                border: '2px solid #f6f6f6',
                borderRadius: '10px',
                boxShadow: '0px 1px 4px 0px #00000040',
              }}
            >
              <Suspense fallback={<SectionFallback height={160} />}>
                <TodayShipment />
                <TrackingStatusUser />
              </Suspense>
            </Box>
            <Suspense fallback={<SectionFallback height={220} />}>
              <UserGeneralOverview />
              <UserDestinations />
            </Suspense>
          </Box>
        )}
      </Box>
    </>
  );
}

export default React.memo(HomeRoutes);
